import {FC, lazy, Suspense} from 'react'
import {Navigate, Route, Routes} from 'react-router-dom'
import TopBarProgress from 'react-topbar-progress-indicator'
import {getCSSVariableValue} from '../../ui/assets/ts/_utils'
import {MasterLayout} from '../../ui/layout/MasterLayout'
import {FilesRouteRedirect} from './FilesRouteRedirect'
import ResultsDeepLink from './ResultsDeepLink'
import {ShareInvitationPage} from '../modules/sharing/ShareInvitationPage'
import {
  DashboardWrapper,
  JobViewerPage,
  PreferencesPage,
  InvitationPage,
  ConnectAuthorizePage,
  WorkflowsPage,
  ToolsPage,
  StatusPage,
} from '../pages'

const PrivateRoutes = () => {
  const ProfilePage = lazy(() => import('../modules/profile/ProfilePage'))

  return (
    <Routes>
      {/* Outside MasterLayout on purpose. This is an OAuth consent screen, usually shown in a small
          popup the client opened: the app chrome around it (chat panel, menus, the conversation the
          user happened to have open) is noise at best, and at worst makes a security decision look
          like part of an unrelated page. */}
      <Route path='connect/authorize' element={<ConnectAuthorizePage />} />
      <Route element={<MasterLayout />}>
        {/* Redirect to Dashboard after success login/registartion */}
        <Route path='auth/*' element={<Navigate to='/dashboard' />} />
        {/* Pages */}
        <Route path='dashboard' element={<DashboardWrapper />} />
        <Route path='tools' element={<ToolsPage />} />
        {/* Files is a lateral panel now — this only honours old /files?path= links. */}
        <Route path='files' element={<FilesRouteRedirect />} />
        <Route path='jobs' element={<JobViewerPage />} />
        {/* Actual ResultsPage (viewer host) is rendered directly in MasterLayout; this
            element only handles the /results?file=<cloudPath> deep link. */}
        <Route path='results' element={<ResultsDeepLink />} />
        <Route path='workflows' element={<WorkflowsPage />} />
        <Route path='admin' element={<PreferencesPage />} />
        {/* Stable link for emails (e.g. the Salesforce low-balance alert): opens Settings with the
            Add Units dialog, where Stripe Checkout also returns. */}
        <Route path='add-units' element={<Navigate to='/admin?recharge=open' replace />} />
        <Route path='invitation' element={<InvitationPage />} />
        <Route path='share-invitation' element={<ShareInvitationPage />} />
        <Route path='status' element={<StatusPage />} />
        {/* Lazy Modules */}
        <Route
          path='crafted/pages/profile/*'
          element={
            <SuspensedView>
              <ProfilePage />
            </SuspensedView>
          }
        />

        {/* Page Not Found */}
        <Route path='*' element={<Navigate to='/error/404' />} />
      </Route>
    </Routes>
  )
}

const SuspensedView: FC<{children?: React.ReactNode}> = ({children}) => {
  const baseColor = getCSSVariableValue('--bs-primary')
  TopBarProgress.config({
    barColors: {
      '0': baseColor,
    },
    barThickness: 1,
    shadowBlur: 5,
  })
  return <Suspense fallback={<TopBarProgress />}>{children}</Suspense>
}

export {PrivateRoutes}
